import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import { styled } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { REGIONS } from "../../../../constants/constants";

const StyledTableCell = styled(TableCell)(({ theme, width = "10rem" }) => ({
  backgroundColor: theme.palette.atlas.extraLight,
  color: theme.palette.primary.main,
  width: width,
}));

const StyledTableCellHead = styled(TableCell)(({ theme, width }) => ({
  backgroundColor: theme.palette.atlas.light,
  color: theme.palette.primary.main,
  width: width,
}));

const StyledTableCellTitle = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.atlas.main,
  color: theme.palette.primary.main,
  textTransform: "capitalize",
  fontSize: "1.2rem",
}));

/**
 * Renders a table of other facts based on the given `factCategory`.
 * @component
 * @category Native
 * @subcategory NationalFactsSheet
 * @param {Object} props - The component props.
 * @param {Function} props.getFacts - A function that returns an object of facts.
 * @param {number} props.factCategory - The category of facts to display.
 * @param {string} props.idForExport - The ID to use for exporting the table.
 * @param {string} props.display - The display style for the table.
 * @param {string} props.region - The path location of the file.
 * @returns {JSX.Element} - The rendered component.
 */

export default function OtherFacts({ getFacts, factCategory, idForExport, display, region }) {
  const otherFacts = getFacts(factCategory);
  const { t } = useTranslation();
  const lowerBoundYear = REGIONS.africa.includes(region) ? 2030 : 2020;

  const colSpan = (factCategory) => {
    switch (factCategory) {
      case "Socio-economic impact assessment" || "Land eligibility" || "Green hydrogen potential":
        return 5;
      case "Renewable energy potential":
        return 3;
      case "Groundwater assessment":
        return REGIONS.africa.includes(region) ? 7 : 4;
      default:
        return 5;
    }
  };
  const RenderLowerBoundGWdata = () => {
    if (REGIONS.africa.includes(region)) {
      return (
        <>
          <StyledTableCellHead align="center" colSpan={1}>
            2020 ({t(`nationalFactsheet.titles.Optimistic`)})
          </StyledTableCellHead>
          <StyledTableCellHead align="center" colSpan={1}>
            2020 ({t(`nationalFactsheet.titles.Pessimistic`)})
          </StyledTableCellHead>
          <StyledTableCellHead align="center" colSpan={1}>
            2030 ({t(`nationalFactsheet.titles.Optimistic`)})
          </StyledTableCellHead>
          <StyledTableCellHead align="center" colSpan={1}>
            2030 ({t(`nationalFactsheet.titles.Pessimistic`)})
          </StyledTableCellHead>
        </>
      );
    } else {
      return (
        <StyledTableCellHead align="center" colSpan={1}>
          2020
        </StyledTableCellHead>
      );
    }
  };

  const RenderGWValues = (props) => {
    const { fact, year } = props;

    if (year === "2020") {
      if (REGIONS.africa.includes(region)) {
        return <RenderOptimisticAndPessimisticValues fact={fact} year={year} />;
      } else {
        return (
          <TableCell align="center" colSpan={1}>
            {fact.value_2020}
          </TableCell>
        );
      }
    } else if (year === "2030") {
      if (REGIONS.africa.includes(region)) {
        return <RenderOptimisticAndPessimisticValues fact={fact} year={year} />;
      } else {
        return <></>;
      }
    } else {
      return <RenderOptimisticAndPessimisticValues fact={fact} year={year} />;
    }
  };

  const RenderOptimisticAndPessimisticValues = (props) => {
    const { fact, year } = props;
    const optimisticValueName = `value_${year}_optimistic`;
    const pessimisticValueName = `value_${year}_pessimistic`;

    return (
      <>
        <TableCell align="center" colSpan={1}>
          {fact[optimisticValueName]}
        </TableCell>
        <TableCell align="center" colSpan={1}>
          {fact[pessimisticValueName]}
        </TableCell>
      </>
    );
  };

  if (otherFacts) {
    return (
      <Paper sx={{ width: "100%", backgroundColor: "unset", display: display }}>
        <TableContainer>
          <Table id={`facts-sheet-table-${idForExport}`} stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCellTitle colSpan={colSpan(factCategory)}>
                  {t(`nationalFactsheet.titles.${otherFacts[factCategory].heading}`)}
                </StyledTableCellTitle>
              </TableRow>
            </TableHead>
            {factCategory === "Green hydrogen potential" && (
              <TableHead>
                <TableRow>
                  <StyledTableCellHead align="center" colSpan={1}></StyledTableCellHead>
                  <StyledTableCellHead align="center" colSpan={1}>
                    100%* {t("layerLegend.layers.group.Hydrogen")}
                  </StyledTableCellHead>
                  <StyledTableCellHead align="center" colSpan={1}>
                    50%* {t("layerLegend.layers.group.Hydrogen")}
                  </StyledTableCellHead>
                  <StyledTableCellHead align="center" colSpan={1}>
                    25%* {t("layerLegend.layers.group.Hydrogen")}
                  </StyledTableCellHead>
                  <StyledTableCellHead align="center" colSpan={1}>
                    1%* {t("layerLegend.layers.group.Hydrogen")}
                  </StyledTableCellHead>
                </TableRow>
              </TableHead>
            )}

            {factCategory === "Groundwater assessment" && (
              <TableHead>
                <TableRow>
                  <StyledTableCellHead align="center" colSpan={1}></StyledTableCellHead>
                  <RenderLowerBoundGWdata />
                  <StyledTableCellHead align="center" colSpan={1}>
                    2050 ({t(`nationalFactsheet.titles.Optimistic`)})
                  </StyledTableCellHead>
                  <StyledTableCellHead align="center" colSpan={1}>
                    2050 ({t(`nationalFactsheet.titles.Pessimistic`)})
                  </StyledTableCellHead>
                </TableRow>
              </TableHead>
            )}

            {factCategory === "Renewable energy potential"
              ? Object.values(otherFacts[factCategory].main).map((fact, index) => (
                  <TableBody key={index}>
                    <TableRow>
                      <StyledTableCellHead align="center" colSpan={1}>
                        {t(`nationalFactsheet.titles.${fact.layer}`)}
                      </StyledTableCellHead>
                      <StyledTableCellHead align="center" colSpan={1}>
                        {lowerBoundYear}
                      </StyledTableCellHead>
                      <StyledTableCellHead align="center" colSpan={1}>
                        2050
                      </StyledTableCellHead>
                    </TableRow>
                    {fact.info.map((item, index) => (
                      <TableRow key={index}>
                        <StyledTableCell colSpan={1} width={undefined}>
                          {t(`nationalFactsheet.titles.${item.title}`)} {item.unit && `[${item.unit}]`}
                          {item.annotation}
                        </StyledTableCell>
                        <TableCell align="center" colSpan={1}>
                          {item.value_2020}
                        </TableCell>

                        <TableCell align="center" colSpan={1}>
                          {item.value_2050}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ))
              : factCategory === "Groundwater assessment"
              ? Object.values(otherFacts[factCategory].main).map((fact, index) => (
                  <TableBody key={index}>
                    <TableRow>
                      <StyledTableCell colSpan={1}>
                        {t(`nationalFactsheet.titles.${fact.title}`)} {fact.unit && `[${fact.unit}]`}
                        {fact.annotation}
                      </StyledTableCell>
                      {["2020", "2030", "2050"].map((year) => (
                        <RenderGWValues fact={fact} year={year} key={year} />
                      ))}
                    </TableRow>
                  </TableBody>
                ))
              : factCategory === "Green hydrogen potential"
              ? Object.values(otherFacts[factCategory].main).map((fact, index) => (
                  <TableBody key={index}>
                    <TableRow>
                      <StyledTableCell>
                        {t(`nationalFactsheet.titles.${fact.title}`)} {fact.year && `(${fact.year})`}{" "}
                        {fact.unit && `[${fact.unit}]`}
                        {fact.annotation}
                      </StyledTableCell>
                      {fact.value.map((value, index) => (
                        <TableCell key={index} align="center">
                          {!value.includes("N/A") ? value : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                ))
              : Object.values(otherFacts[factCategory].main).map((fact, index) => (
                  <TableBody key={index}>
                    <TableRow>
                      <StyledTableCell colSpan={2}>
                        {t(`nationalFactsheet.titles.${fact.title}`)}
                        <sup>{fact.annotation}</sup> {fact.year && `(${fact.year})`}{" "}
                        {fact.unit && `[${fact.unit}]`}
                      </StyledTableCell>

                      <TableCell colSpan={3}>{fact.value}</TableCell>
                    </TableRow>
                  </TableBody>
                ))}
          </Table>
          {/* <CostCurve
            idForExport={idForExport}
            data={otherFacts[factCategory].potentialCurve}
          /> */}
          {otherFacts[factCategory].preFootnotes && otherFacts[factCategory].preFootnotes.length > 0 && (
            <Table
              sx={{ width: "fit-content", mb: 2 }}
              id={`facts-sheet-preFootnotes-${idForExport}`}
              size="small"
            >
              <TableFooter>
                {Object.values(otherFacts[factCategory].preFootnotes).map((note, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontStyle: "italic", color: "#000000" }}>
                      <li style={{ listStyle: "none" }}>
                        {t(`nationalFactsheet.preFootnotes.${note.label}`)}
                      </li>
                    </TableCell>
                  </TableRow>
                ))}
              </TableFooter>
            </Table>
          )}
          {otherFacts[factCategory].footnotes && (
            <Table
              sx={{ width: "fit-content", mt: 2 }}
              id={`facts-sheet-footnote-${idForExport}`}
              size="small"
            >
              <TableFooter>
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic", fontWeight: 900 }} colSpan={3}>
                    {t("nationalFactsheet.titles.Notes")}
                  </TableCell>
                </TableRow>
                {Object.values(otherFacts[factCategory].footnotes).map((note, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontStyle: "italic", color: "#000000" }}>
                      {" "}
                      <li style={{ listStyle: "none" }}>
                        {" "}
                        {note.noteAnnotation && note.noteAnnotation}{" "}
                        {t(`nationalFactsheet.footnotes.${note.label}`)}
                      </li>{" "}
                    </TableCell>
                  </TableRow>
                ))}
              </TableFooter>
            </Table>
          )}
          {otherFacts[factCategory].references && (
            <Table
              sx={{ width: "fit-content", mt: 2 }}
              id={`facts-sheet-reference-${idForExport}`}
              size="small"
            >
              <TableFooter>
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic", fontWeight: 900 }} colSpan={3}>
                    {t("nationalFactsheet.titles.References")}
                  </TableCell>
                </TableRow>
                {Object.values(otherFacts[factCategory].references).map((reference, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontStyle: "italic", color: "#000000" }}>
                      {" "}
                      <li style={{ listStyle: "none" }}>
                        {" "}
                        [{reference.referenceAnnotation}] {reference.label}
                      </li>{" "}
                    </TableCell>
                  </TableRow>
                ))}
              </TableFooter>
            </Table>
          )}
        </TableContainer>
      </Paper>
    );
  } else {
    return <></>;
  }
}
